import { lazy, Suspense, type ComponentType } from "react";

/**
 * Resilient lazy loader. Retries dynamic imports on transient network failures
 * (e.g. Vite dev-server restarts, stale chunks after a deploy). On the final
 * attempt, force a one-time hard reload so the user is never stuck on a blank
 * page caused by a single failed chunk fetch.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 400,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastErr: unknown;
    for (let i = 0; i <= retries; i++) {
      try {
        return await factory();
      } catch (err) {
        lastErr = err;
        await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      }
    }
    // Final fallback: hard refresh once to recover from stale/missing chunks.
    if (typeof window !== "undefined") {
      const KEY = "__chunk_reload__";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
        // Return a never-resolving promise while the page reloads.
        return new Promise(() => {}) as never;
      }
      sessionStorage.removeItem(KEY);
    }
    throw lastErr;
  });
}
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ActiveProjectProvider } from "@/contexts/ActiveProjectContext";
import { NotesPanelProvider } from "@/contexts/NotesPanelContext";
import { HelpPanelProvider } from "@/contexts/HelpPanelContext";
import { ProcessingQueueProvider } from "@/contexts/ProcessingQueueContext";
import { TourProvider } from "@/contexts/TourContext";
import { AiGatewayHealthProvider } from "@/contexts/AiGatewayHealthContext";
import { AiGatewayBanner } from "@/components/system/AiGatewayBanner";

// Eagerly import landing page for fastest FCP/LCP
import Index from "./pages/Index";

// Lazy-load all other pages so their CSS splits into separate chunks
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const UserRoleScreen = lazyWithRetry(() => import("./components/onboarding/UserRoleScreen"));
const OnboardingFlow = lazyWithRetry(() => import("./pages/OnboardingFlow"));
const OnboardingUpload = lazyWithRetry(() => import("./pages/OnboardingUpload"));
const Projects = lazyWithRetry(() => import("./pages/Projects"));
const ProjectView = lazyWithRetry(() => import("./pages/ProjectView"));
const TranscriptLibrary = lazyWithRetry(() => import("./pages/TranscriptLibrary"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const ResearchAdvisor = lazyWithRetry(() => import("./pages/ResearchAdvisor"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const CheckoutSuccess = lazyWithRetry(() => import("./pages/CheckoutSuccess"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const Inbox = lazyWithRetry(() => import("./pages/Inbox"));

const Findings = lazyWithRetry(() => import("./pages/Findings"));
const Draft = lazyWithRetry(() => import("./pages/Draft"));
const WhatIsVerithos = lazyWithRetry(() => import("./pages/WhatIsVerithos"));
const SampleReport = lazyWithRetry(() => import("./pages/SampleReport"));
const HowToCite = lazyWithRetry(() => import("./pages/HowToCite"));
const VCardPage = lazyWithRetry(() => import("./pages/VCardPage"));
const VBoardPage = lazyWithRetry(() => import("./pages/VBoardPage"));
const VDraftPage = lazyWithRetry(() => import("./pages/VDraftPage"));
const VerithosVsLLMs = lazyWithRetry(() => import("./pages/VerithosVsLLMs"));
const QdasComparison = lazyWithRetry(() => import("./pages/QdasComparison"));
const Whitepaper = lazyWithRetry(() => import("./pages/Whitepaper"));
const Resources = lazyWithRetry(() => import("./pages/Resources"));
const Guide = lazyWithRetry(() => import("./pages/Guide"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Feedback = lazyWithRetry(() => import("./pages/Feedback"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const InvitePage = lazyWithRetry(() => import("./pages/InvitePage"));

import { ScrollToTop } from "./components/ScrollToTop";
import { RouteLoader } from "./components/layout/RouteLoader";

// Query client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
      refetchOnReconnect: true,
    },
  },
});

/**
 * AuthenticatedShell — wraps app routes with heavy providers
 * (Subscription, ActiveProject, ProcessingQueue).
 * Public routes skip these entirely for faster load.
 */
function AuthenticatedShell() {
  return (
    <SubscriptionProvider>
      <ActiveProjectProvider>
        <NotesPanelProvider>
          <HelpPanelProvider>
            <ProcessingQueueProvider>
              <TourProvider>
                <AiGatewayHealthProvider>
                  <AiGatewayBanner />
                  <Outlet />
                </AiGatewayHealthProvider>
              </TourProvider>
            </ProcessingQueueProvider>
          </HelpPanelProvider>
        </NotesPanelProvider>
      </ActiveProjectProvider>
    </SubscriptionProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              {/* ── Public routes — no heavy providers ── */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/logout" element={<Auth />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/terms-of-service" element={<Terms />} />
              <Route path="/what-is-verithos" element={<WhatIsVerithos />} />
              <Route path="/sample-draft" element={<SampleReport />} />
              <Route path="/sample-report" element={<Navigate to="/sample-draft" replace />} />
              <Route path="/how-to-cite" element={<HowToCite />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/help" element={<Navigate to="/guide" replace />} />
              <Route path="/docs" element={<Navigate to="/guide" replace />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/verithos-vs-llms" element={<VerithosVsLLMs />} />
              <Route path="/verithos-vs-chatgpt" element={<Navigate to="/verithos-vs-llms" replace />} />
              <Route path="/verithos-vs-nvivo-atlasti-maxqda-dedoose" element={<QdasComparison />} />
              <Route path="/whitepaper" element={<Whitepaper />} />
              <Route path="/whitepaper/workflow" element={<Navigate to="/whitepaper#workflow" replace />} />
              <Route path="/whitepaper/standards" element={<Navigate to="/whitepaper#standards" replace />} />
              <Route path="/whitepaper/references" element={<Navigate to="/whitepaper#references" replace />} />
              <Route path="/whitepaper/changelog" element={<Navigate to="/whitepaper#references" replace />} />
              <Route path="/v/card/:token" element={<VCardPage />} />
              <Route path="/v/board/:token" element={<VBoardPage />} />
              <Route path="/v/draft/:token" element={<VDraftPage />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />

              {/* ── Authenticated routes — wrapped in heavy providers ── */}
              <Route element={<AuthenticatedShell />}>
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/onboarding/role" element={<UserRoleScreen />} />
                <Route path="/onboarding/start" element={<OnboardingFlow />} />
                <Route path="/onboarding/upload" element={<OnboardingUpload />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/home" element={<Navigate to="/projects" replace />} />
                <Route path="/dashboard" element={<Navigate to="/projects" replace />} />
                <Route path="/project/:slug" element={<ProjectView />} />
                <Route path="/transcripts" element={<TranscriptLibrary />} />
                <Route path="/codebook" element={<Navigate to="/findings?tab=findings" replace />} />
                <Route path="/findings" element={<Findings />} />
                <Route path="/draft" element={<Draft />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/research-advisor" element={<ResearchAdvisor />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
